import { useMemo } from "react";
import { useLocale } from "next-intl";
import { useStore } from "@store/index";
import type { LineItem } from "@graphql/generated/types";
import { getCountryForSiteByLocale } from "@config/site/site-config";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { useDeliveryEstimate } from "@components/commerce/product/accordion-single-product/hooks/useDeliveryEstimate";
import {
    formatEstimatedDeliveryDate,
    pickEstimatedDeliveryDate,
} from "@lib/utils/pdp/estimatedDelivery";
import type { ReactNode } from "react";
import { CommonCMS, ShoppingBag } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ecco/ui/text";

type UseEstimatedDeliveryDateOptions = {
    lineItems?: LineItem[];
    skus?: string[];
    // Set to false to skip the estimate entirely, e.g. when the cart is Click & Collect
    // and a shipping delivery date is not applicable. Defaults to true.
    enabled?: boolean;
};

// Shared by useEstimatedDeliveryDate and useIsEstimatedDeliveryDateLoading so both hooks
// resolve the same skus and hit the same react-query cache entry instead of duplicating logic.
const useDeliveryEstimateForCart = ({
    lineItems: lineItemsOverride,
    skus: skusOverride,
    enabled = true,
}: UseEstimatedDeliveryDateOptions) => {
    const locale = useLocale();
    const hasEstimatedDeliveryDate = useFeatureFlag("hasEstimatedDeliveryDate") && enabled;

    const cart = useStore((state) => state.data.me?.cartInfo?.cart);
    const lineItems = lineItemsOverride ?? cart?.lineItems;

    const deliveryEstimateSkus = useMemo(() => {
        if (skusOverride) return Array.from(new Set(skusOverride.filter(Boolean)));
        return Array.from(new Set(lineItems?.map((item) => item.variant?.sku).filter(Boolean)));
    }, [lineItems, skusOverride]);

    // useDeliveryEstimate already no-ops when skus/country are empty.
    const { data: deliveryEstimates, isLoading } = useDeliveryEstimate({
        skus: deliveryEstimateSkus,
        country: getCountryForSiteByLocale(locale),
        enabled: hasEstimatedDeliveryDate,
    });

    return { locale, hasEstimatedDeliveryDate, deliveryEstimateSkus, deliveryEstimates, isLoading };
};

// Raw earliest/latest ISO dates behind the same feature-flag/completeness gating as
// useEstimatedDeliveryDate. Exposed separately since some callers (e.g. deciding whether
// express delivery is still worth showing) need the ISO date, not the display string.
export const useEstimatedDeliveryRange = (
    options: UseEstimatedDeliveryDateOptions = {}
): { earliest: string; latest?: string } | null => {
    const { hasEstimatedDeliveryDate, deliveryEstimateSkus, deliveryEstimates } =
        useDeliveryEstimateForCart(options);

    return useMemo(() => {
        if (!hasEstimatedDeliveryDate || deliveryEstimateSkus.length === 0) return null;

        // A response missing even one requested sku must not be treated as complete.
        // Otherwise pickEstimatedDeliveryDate could format another sku's date for the whole bag.
        const hasEveryEstimate = deliveryEstimateSkus.every((sku) =>
            deliveryEstimates?.some(
                (estimate) => estimate.sku === sku && Boolean(estimate.estimate?.deliveryDate)
            )
        );
        if (!hasEveryEstimate) return null;

        return pickEstimatedDeliveryDate(deliveryEstimates);
    }, [hasEstimatedDeliveryDate, deliveryEstimateSkus, deliveryEstimates]);
};

// Defaults to the current cart. Pass `lineItems` or `skus` to estimate a different set instead,
// e.g. a placed order's items, since the cart is cleared once the confirmation page loads.
export const useEstimatedDeliveryDate = (
    options: UseEstimatedDeliveryDateOptions = {}
): string | null => {
    const locale = useLocale();
    const range = useEstimatedDeliveryRange(options);

    return useMemo(() => {
        if (!range) return null;
        // pickEstimatedDeliveryDate already omits `latest` when it equals `earliest`,
        // so this naturally renders as a range only when there's an actual gap.
        return formatEstimatedDeliveryDate(range, locale);
    }, [range, locale]);
};

// True while the estimate is in flight, for callers that want to show a skeleton instead of
// a popping-in label (CLS). Also checks hasEstimatedDeliveryDate/skus so a disabled flag or
// empty cart never gets stuck on a skeleton that will never resolve.
export const useIsEstimatedDeliveryDateLoading = (
    options: UseEstimatedDeliveryDateOptions = {}
): boolean => {
    const { hasEstimatedDeliveryDate, deliveryEstimateSkus, isLoading } =
        useDeliveryEstimateForCart(options);

    return hasEstimatedDeliveryDate && deliveryEstimateSkus.length > 0 && isLoading;
};

// Wraps an already-formatted delivery date in the "expected delivery" microcopy template.
export const useExpectedDeliveryText = (
    date: string | null,
    text: string = ShoppingBag.expectedDeliveryControl
): ReactNode | null => {
    const { get: getMicrocopy } = useMicrocopy();

    if (!date) return null;

    return getMicrocopy(CommonCMS.page, text, {
        replacements: {
            date: (
                <Text as="span" fontWeight="bold">
                    {date}
                </Text>
            ),
        },
        jsx: true,
    });
};
